import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Upload, Check, DownloadSimple } from '@phosphor-icons/react';
import {
  listPalletReconciliations, createPalletReconciliation,
  getPalletReconciliation, signoffPalletReconciliation,
  list3plMerchants, palletReconciliationCsvUrl,
  getPalletReconciliationPrefill,
} from '../../lib/api';
import { fmtDate } from './utils';

/**
 * Reconciliation tab — match a CHEP/Loscam statement against system stock.
 *
 * The user can either paste lines into the Lines table (one per merchant)
 * or paste a CSV with `merchant_name,statement_qty` rows. Variance is
 * computed server-side against the live `pallet_movements` aggregation.
 */
export default function ReconciliationTab({ types }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailRec, setDetailRec] = useState(null);
  const [form, setForm] = useState({
    type_id: '', provider: 'CHEP',
    statement_date: new Date().toISOString().slice(0, 10),
    csv: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchRecs = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        listPalletReconciliations(),
        list3plMerchants().catch(() => ({ data: [] })),
      ]);
      setRecs(r.data || []);
      setMerchants(m.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load reconciliations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRecs(); }, []);

  const beginCreate = () => {
    setForm({
      type_id: types.find((t) => t.is_pooled && t.is_active)?.id || '',
      provider: 'CHEP',
      statement_date: new Date().toISOString().slice(0, 10),
      csv: '', notes: '',
    });
    setOpen(true);
  };

  const parseCsv = (text) => {
    return (text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => {
      const [a, b] = line.split(/[,\t]/).map((s) => s.trim());
      if (!a) return null;
      // If first cell is purely numeric, treat as merchant_id (less common); otherwise name.
      const looksLikeId = /^[a-f0-9]{24}$/i.test(a);
      return looksLikeId
        ? { merchant_id: a, statement_qty: Number(b || 0) }
        : { merchant_name: a, statement_qty: Number(b || 0) };
    }).filter(Boolean);
  };

  const submit = async () => {
    if (!form.type_id) return toast.error('Pick a pallet type');
    const lines = parseCsv(form.csv);
    if (lines.length === 0) return toast.error('Paste at least one line');
    setSaving(true);
    try {
      const payload = {
        type_id: form.type_id, provider: form.provider,
        statement_date: new Date(form.statement_date).toISOString(),
        lines,
      };
      if (form.notes.trim()) payload.notes = form.notes.trim();
      await createPalletReconciliation(payload);
      toast.success('Reconciliation saved');
      setOpen(false);
      await fetchRecs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (rec) => {
    try {
      const r = await getPalletReconciliation(rec.id);
      setDetailRec(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load detail');
    }
  };

  const signOff = async (rec) => {
    if (!window.confirm(`Sign off ${rec.provider} statement dated ${fmtDate(rec.statement_date)}?`)) return;
    try {
      const r = await signoffPalletReconciliation(rec.id);
      toast.success('Signed off');
      setDetailRec(r.data);
      await fetchRecs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sign-off failed');
    }
  };

  const buildCsvHelper = () =>
    merchants.map((m) => `${m.name},0`).join('\n');

  const prefillFromInvestigator = async () => {
    if (!form.type_id) { toast.error('Pick a pallet type first'); return; }
    try {
      const { data } = await getPalletReconciliationPrefill({ type_id: form.type_id });
      if (!data.line_count) {
        toast.info('No merchants with outstanding pallets for this type.');
        return;
      }
      setForm((f) => ({ ...f, csv: data.csv }));
      toast.success(`Prefilled ${data.line_count} merchants from Investigator — adjust each qty to match the statement`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Prefill failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">Reconciliation</h2>
          <p className="text-xs text-muted-foreground">
            Compare CHEP / Loscam statements against your system balance per merchant.
          </p>
        </div>
        <Button onClick={beginCreate} size="sm" data-testid="pallet-rec-create-btn">
          <Plus size={14} className="mr-1" /> New reconciliation
        </Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2">Statement date</th>
              <th className="p-2">Provider</th>
              <th className="p-2">Type</th>
              <th className="p-2 text-right">Lines</th>
              <th className="p-2 text-right">Total variance</th>
              <th className="p-2">Status</th>
              <th className="p-2 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && recs.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                No reconciliations yet — pull a CHEP/Loscam statement and paste it in.
              </td></tr>
            )}
            {!loading && recs.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer"
                onClick={() => openDetail(r)} data-testid={`pallet-rec-row-${r.id}`}>
                <td className="p-2 whitespace-nowrap">{fmtDate(r.statement_date)}</td>
                <td className="p-2">{r.provider}</td>
                <td className="p-2 font-mono text-xs">{r.type_code}</td>
                <td className="p-2 text-right font-mono">{r.lines_count}</td>
                <td className={`p-2 text-right font-mono font-bold ${r.total_variance < 0 ? 'text-rose-700' : r.total_variance > 0 ? 'text-emerald-700' : ''}`}>
                  {r.total_variance > 0 ? '+' : ''}{r.total_variance}
                </td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${r.status === 'signed_off' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {r.status === 'signed_off' ? 'Signed off' : 'Draft'}
                  </span>
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDetail(r); }} data-testid={`pallet-rec-view-${r.id}`}>View</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="pallet-rec-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload size={18} /> New reconciliation</DialogTitle>
            <DialogDescription>
              Paste statement lines (one per merchant). Variance is computed against live pallet movements.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Pallet type *</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })}
                  data-testid="rec-form-type">
                  <option value="">Select…</option>
                  {types.filter((t) => t.is_active).map((t) =>
                    <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Provider</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  data-testid="rec-form-provider">
                  <option value="CHEP">CHEP</option>
                  <option value="Loscam">Loscam</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div>
                <Label>Statement date *</Label>
                <Input type="date" value={form.statement_date}
                  onChange={(e) => setForm({ ...form, statement_date: e.target.value })}
                  data-testid="rec-form-date" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Lines (one per row · format: <code>merchant_name,qty</code>)</Label>
                <div className="flex items-center gap-3">
                  <button type="button" className="text-[11px] underline text-brand"
                    onClick={prefillFromInvestigator}
                    data-testid="rec-form-prefill-investigator"
                    title="Loads every merchant with outstanding pallets for this type — their system net-outstanding becomes the starting qty. Overwrite each with the number reported on the CHEP/Loscam statement.">
                    Prefill from Investigator ↩
                  </button>
                  <button type="button" className="text-[11px] underline text-muted-foreground"
                    onClick={() => setForm({ ...form, csv: buildCsvHelper() })}
                    data-testid="rec-form-fill-merchants">
                    Pre-fill from active merchants
                  </button>
                </div>
              </div>
              <textarea value={form.csv} onChange={(e) => setForm({ ...form, csv: e.target.value })}
                className="w-full border border-border bg-background p-2 text-sm font-mono" rows={8}
                placeholder={'Acme Co,42\nBrandX Pty Ltd,17\n…'}
                data-testid="rec-form-csv" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="rec-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="rec-form-submit">
              {saving ? 'Saving…' : 'Save reconciliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailRec} onOpenChange={(v) => !v && setDetailRec(null)}>
        <DialogContent className="max-w-3xl" data-testid="pallet-rec-detail-dialog">
          {detailRec && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {detailRec.provider} · {detailRec.type_code} · {fmtDate(detailRec.statement_date)}
                </DialogTitle>
                <DialogDescription>
                  {detailRec.lines_count} line(s) · total variance{' '}
                  <strong className={detailRec.total_variance < 0 ? 'text-rose-700' : detailRec.total_variance > 0 ? 'text-emerald-700' : ''}>
                    {detailRec.total_variance > 0 ? '+' : ''}{detailRec.total_variance}
                  </strong>
                </DialogDescription>
              </DialogHeader>
              <div className="border border-border bg-card max-h-[400px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border sticky top-0">
                    <tr className="text-left">
                      <th className="p-2">Merchant</th>
                      <th className="p-2 text-right">Statement qty</th>
                      <th className="p-2 text-right">System qty</th>
                      <th className="p-2 text-right">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailRec.lines || []).map((ln, i) => (
                      <tr key={i} className="border-b border-border last:border-b-0">
                        <td className="p-2">{ln.merchant_name || ln.merchant_id || '—'}</td>
                        <td className="p-2 text-right font-mono">{ln.statement_qty}</td>
                        <td className="p-2 text-right font-mono">{ln.system_qty}</td>
                        <td className={`p-2 text-right font-mono font-bold ${ln.variance < 0 ? 'text-rose-700' : ln.variance > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                          {ln.variance > 0 ? '+' : ''}{ln.variance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detailRec.notes && <div className="text-xs text-muted-foreground">Notes: {detailRec.notes}</div>}
              <DialogFooter>
                <a href={palletReconciliationCsvUrl(detailRec.id, 'portfolio_plus')}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs px-3 py-2 border border-border hover:bg-muted/40"
                  data-testid="pallet-rec-export-audit">
                  <DownloadSimple size={14} /> Audit CSV
                </a>
                <a href={palletReconciliationCsvUrl(detailRec.id, 'transfer')}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs px-3 py-2 border border-border hover:bg-muted/40"
                  data-testid="pallet-rec-export-transfer">
                  <DownloadSimple size={14} /> Transfer CSV (CHEP)
                </a>
                <Button variant="outline" onClick={() => setDetailRec(null)}>Close</Button>
                {detailRec.status !== 'signed_off' && (
                  <Button onClick={() => signOff(detailRec)} data-testid="pallet-rec-signoff">
                    <Check size={14} className="mr-1" /> Sign off
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
